import { useEffect, useRef, useState } from 'react';

type Axis = 'x' | 'y' | 'both';

const isInteractive = (t: EventTarget | null) =>
  t instanceof Element &&
  !!t.closest(
    'button, a, input, textarea, select, [contenteditable="true"], [data-no-drag]'
  );

const DEFAULT_DRAG_THRESHOLD = 5;

export type UseDragScrollOptions = {
  /** Pixels the pointer must move before treating as drag (avoids suppressing clicks). Default 5. */
  dragThreshold?: number;
};

export function useDragScroll(axis: Axis = 'x', options: UseDragScrollOptions = {}) {
  const { dragThreshold = DEFAULT_DRAG_THRESHOLD } = options;
  const ref = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const start = useRef({ x: 0, y: 0, sx: 0, sy: 0 });
  const dragged = useRef(false);
  const active = useRef(false); // are we in a drag session?

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (isInteractive(e.target)) {
        dragged.current = false;
        active.current = false;
        return; // let buttons/links work
      }

      active.current = true;
      dragged.current = false;

      start.current = {
        x: e.clientX,
        y: e.clientY,
        sx: el.scrollLeft,
        sy: el.scrollTop,
      };

      setIsDragging(false); // only set true after we pass threshold
      e.preventDefault(); // stop selection/gesture
    };

    const onMove = (e: PointerEvent) => {
      if (!active.current) return;
      const dx = e.clientX - start.current.x;
      const dy = e.clientY - start.current.y;

      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < dragThreshold) return;

      if (!dragged.current) {
        dragged.current = true;
        setIsDragging(true);
      }

      if (axis === 'x' || axis === 'both')
        el.scrollLeft = start.current.sx - dx;
      if (axis === 'y' || axis === 'both') el.scrollTop = start.current.sy - dy;

      e.preventDefault(); // needs passive: false on the listener
    };

    const onEnd = () => {
      if (!active.current) return;
      active.current = false;
      setIsDragging(false);
      // keep dragged.current for onClickCapture
    };

    el.addEventListener('pointerdown', onDown);
    document.addEventListener('pointermove', onMove, { passive: false });
    document.addEventListener('pointerup', onEnd);
    document.addEventListener('pointercancel', onEnd);

    return () => {
      el.removeEventListener('pointerdown', onDown);
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onEnd);
      document.removeEventListener('pointercancel', onEnd);
    };
  }, [axis, dragThreshold]);

  // cancel only the synthetic click that follows a drag
  const onClickCapture = (e: React.SyntheticEvent) => {
    if (dragged.current) {
      e.preventDefault();
      e.stopPropagation();
      dragged.current = false;
    }
  };

  const dragScrollStyle = {
    overscrollBehaviorX: 'contain',
    touchAction: 'none',
    WebkitOverflowScrolling: 'touch',
    cursor: isDragging ? 'grabbing' : 'grab',
  } as const;

  return { ref, onClickCapture, isDragging, dragScrollStyle };
}
